/**
 * Bundle bootstrap: replaces the inline `<script>` blocks that used to
 * live in index.html (auto-mount, _cbCopy clipboard handler).
 *
 * Moving these here lets the CSP `script-src` directive drop
 * `'unsafe-inline'`. Imported for side-effect from src/index.ts.
 */

import { mountApp } from './app.js'
import { showToast } from './components/shared.js'

// _cbCopy: navigator.clipboard handler invoked from the inline `onclick`
// in components/shared.ts:codeBlock(). Inline event-handler attributes
// are still permitted by CSP via 'unsafe-hashes' (see server/api/app.ts);
// what we eliminate by defining the symbol here is the entire
// <script>...</script> shim block in index.html.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(window as any)._cbCopy = function (id: string): void {
  const el = document.getElementById(id)
  if (!el) {
    return
  }
  const text = el.getAttribute('data-raw') ?? el.textContent ?? ''
  const done = (): void => {
    showToast('Copied', 'info')
  }
  const fail = (): void => {
    showToast('Copy failed', 'error')
  }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(fail)
  } else {
    fail()
  }
}

// Auto-mount on DOM ready. Replaces the inline `<script>` in index.html
// that called mountApp(). Idempotent: a second invocation (e.g. from the
// e2e mock-server's legacy inline trigger) is a no-op via the guard.
let _appMounted = false
function _autoMount(): void {
  if (_appMounted) {
    return
  }
  _appMounted = true
  void mountApp().catch((err) => {
    console.error('[grizzly-web] mountApp failed:', err)
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _autoMount, { once: true })
} else {
  _autoMount()
}
